import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { CardControl, CardControlRadio, labelText } from '../../../shared/models/CardControl';
import { StandingAuthorizationCard } from '../../../shared/components/standing-authorization-card-container/standing-authorization-card-container.component';
import { ActionCenterRedirectService } from '../../../shared/services/ActionCenterRedirect.service';
import { StandingInstruction } from '../../../shared/types/StandingInstruction';
import { transactionDirection } from '../../../shared/utilities/Constants';
import { selectEnableUseSloaContainer } from '../../../feature-flags/reducers';

@Component({
  standalone: false,
  selector: 'mm-sa-ach-source-of-funds',
  templateUrl: './sa-ach-source-of-funds.component.html',
  styleUrls: ['./sa-ach-source-of-funds.component.scss'],
})
export class SaAchSourceOfFundsComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() instructions$!: Observable<StandingInstruction[]>;
  @Input() directionSelection$!: Observable<'incoming' | 'outgoing' | ''>;
  directionSelection: string = '';
  enableUseSloaContainer: boolean = false;
  standingAuthCards: StandingAuthorizationCard[] = [];

  private destroy$ = new Subject<void>();
  standingAuthIdModel: CardControl = {
    title: 'Source of funds',
    controlName: 'standingAuthId',
    radios: [],
    validationMessage: 'Select a source of funds',
  };

  constructor(
    private actionCenterRedirectService: ActionCenterRedirectService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.store.select(selectEnableUseSloaContainer).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.enableUseSloaContainer = val;
    });

    this.directionSelection$.pipe(
      takeUntil(this.destroy$),
      filter(val => val !== ''),
      map((val) => {
        this.directionSelection = val;
        this.standingAuthIdModel.title = this.isAchIn ? 'Source of funds' : 'Account to receive funds';
        this.standingAuthIdModel.validationMessage = this.isAchIn ? 'Select a source of funds' : 'Select an account to receive funds';
        return val === transactionDirection.in.toLowerCase()
      }),
      mergeMap((showIncoming) => {
        if (showIncoming) {
          return this.instructions$?.pipe(
            map((instructions) => {
              return instructions.filter(instruction => instruction.fundsDirection === transactionDirection.in)
            })
          ) ?? []
        }
        return this.instructions$?.pipe(
          map((instructions) => {
            return instructions.filter(instruction => instruction.fundsDirection === transactionDirection.out)
          })
        ) ?? []
      }),
    ).subscribe((instructions) => {
        const newRadios: CardControlRadio[] = [];
        instructions.forEach((instruction) => {
          const labelArr: labelText[] = [];
          
          labelArr.push({ text: instruction.to1 });
          labelArr.push({ label: 'Account number: ', text: instruction.to2 });
          labelArr.push({ text: `${instruction.isRecurring ? 'Recurring' : 'On-Request'} - ${instruction.isThirdParty ? '3rd party' : '1st party'}` })
          if (instruction.nickname) {
            labelArr.push({ text: instruction.nickname });
          }

          newRadios.push({
            value: `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`,
            label: labelArr,
          });
        });
        this.standingAuthIdModel.radios = newRadios;
        if (this.enableUseSloaContainer && newRadios.length > 0) {
          this.updateStandingAuthorizationCards(newRadios);
        }
      }
    );
  }

  private updateStandingAuthorizationCards(radios: CardControlRadio[]): void {
    this.standingAuthCards = radios?.map((radio) => ({
      id: radio.value,
      title: radio.label?.[0]?.text || '',
      subtitle: radio.label?.[1]?.text ? `Account number: ${radio.label?.[1]?.text}` : '',
      details: [radio.label?.[2]?.text || '', radio.label?.[3]?.text || ''].filter(Boolean),
      selected: this.parentForm?.get('standingAuthId')?.value === radio.value,
    })) ?? [];
  }

  onStandingAuthCardSelected(cardId: string): void {
    if (this.parentForm?.get('standingAuthId')?.disabled) {
      return;
    }
    this.parentForm?.get('standingAuthId')?.setValue(cardId);
    this.parentForm?.get('standingAuthId')?.markAsTouched();
    this.standingAuthCards = this.standingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  actionCenterRedirect() {
    this.actionCenterRedirectService.redirectToActionCenter();
  }

  get isAchIn(): boolean {
    return this.directionSelection === transactionDirection.in.toLowerCase();
  }

  get isStandingAuthIdDisabled(): boolean {
    return this.parentForm?.get('standingAuthId')?.disabled ?? false;
  }
}